import React from 'react';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import { Layout } from './components/Layout';
import { Home } from './pages/Home';
import { Characters } from './pages/Characters';
import { Quotes } from './pages/Quotes';
import { Quiz, type CharacterName } from './pages/Quiz';
import { LanguageProvider } from './contexts/LanguageContext';

function App() {
  const questions = [
    {
      question: "daily",
      options: {
        en: ["Play pranks", "Study", "Take care of family", "Daydream", "Solve problems", "Seek freedom"],
        zh: ["恶作剧", "学习", "照顾家人", "做白日梦", "解决问题", "追求自由"],
        ru: ["Устраивать розыгрыши", "Учиться", "Заботиться о семье", "Мечтать", "Решать проблемы", "Искать свободу"],
        fr: ["Faire des farces", "Étudier", "S'occuper de la famille", "Rêvasser", "Résoudre des problèmes", "Chercher la liberté"],
        de: ["Streiche spielen", "Lernen", "Sich um die Familie kümmern", "Tagträumen", "Probleme lösen", "Freiheit suchen"],
        nl: ["Grappen uithalen", "Studeren", "Voor familie zorgen", "Dagdromen", "Problemen oplossen", "Vrijheid zoeken"],
        ms: ["Buat jenaka", "Belajar", "Jaga keluarga", "Berkhayal", "Selesaikan masalah", "Cari kebebasan"]
      },
      characters: ["Reese", "Malcolm", "Lois", "Dewey", "Hal", "Francis"]
    },
    {
      question: "difficulty",
      options: {
        en: ["Find a solution", "Complain about unfairness", "Seek help", "Pretend not to see", "Solve creatively", "Run away"],
        zh: ["想办法解决", "抱怨生活不公", "寻求帮助", "装作没看见", "用创意方式解决", "逃避"],
        ru: ["Найти решение", "Жаловаться на несправедливость", "Искать помощь", "Притвориться слепым", "Решать творчески", "Убежать"],
        fr: ["Trouver une solution", "Se plaindre de l'injustice", "Chercher de l'aide", "Faire semblant de ne pas voir", "Résoudre créativement", "S'enfuir"],
        de: ["Eine Lösung finden", "Sich über Ungerechtigkeit beschweren", "Hilfe suchen", "So tun als sähe man nichts", "Kreativ lösen", "Weglaufen"],
        nl: ["Een oplossing vinden", "Klagen over oneerlijkheid", "Hulp zoeken", "Doen alsof je niets ziet", "Creatief oplossen", "Wegrennen"],
        ms: ["Cari penyelesaian", "Rungut tentang ketidakadilan", "Cari bantuan", "Buat-buat tak nampak", "Selesai secara kreatif", "Melarikan diri"]
      },
      characters: ["Lois", "Malcolm", "Dewey", "Reese", "Hal", "Francis"]
    },
    {
      question: "weekend",
      options: {
        en: ["Watch TV", "Do experiments", "Go on adventures", "Practice talents", "Take care of family", "Seek thrills"],
        zh: ["看电视", "做实验", "外出冒险", "练习才艺", "照顾家人", "寻找刺激"],
        ru: ["Смотреть ТВ", "Проводить эксперименты", "Искать приключения", "Развивать таланты", "Заботиться о семье", "Искать острых ощущений"],
        fr: ["Regarder la TV", "Faire des expériences", "Partir à l'aventure", "Pratiquer ses talents", "S'occuper de la famille", "Chercher des sensations"],
        de: ["Fernsehen", "Experimente machen", "Auf Abenteuer gehen", "Talente üben", "Familie versorgen", "Nervenkitzel suchen"],
        nl: ["TV kijken", "Experimenten doen", "Op avontuur gaan", "Talenten oefenen", "Voor familie zorgen", "Spanning zoeken"],
        ms: ["Tonton TV", "Buat eksperimen", "Pergi mencabar", "Berlatih bakat", "Jaga keluarga", "Cari keseronokan"]
      },
      characters: ["Hal", "Malcolm", "Francis", "Dewey", "Lois", "Reese"]
    },
    {
      question: "strength",
      options: {
        en: ["Creativity", "Wisdom", "Adaptability", "Courage", "Responsibility", "Independence"],
        zh: ["创造力", "智慧", "适应能力", "勇气", "责任心", "独立性"],
        ru: ["Креативность", "Мудрость", "Адаптивность", "Смелость", "Ответственность", "Независимость"],
        fr: ["Créativité", "Sagesse", "Adaptabilité", "Courage", "Responsabilité", "Indépendance"],
        de: ["Kreativität", "Weisheit", "Anpassungsfähigkeit", "Mut", "Verantwortung", "Unabhängigkeit"],
        nl: ["Creativiteit", "Wijsheid", "Aanpassingsvermogen", "Moed", "Verantwoordelijkheid", "Onafhankelijkheid"],
        ms: ["Kreativiti", "Kebijaksanaan", "Kebolehsuaian", "Keberanian", "Tanggungjawab", "Kebebasan"]
      },
      characters: ["Hal", "Malcolm", "Dewey", "Reese", "Lois", "Francis"]
    },
    {
      question: "familyRole",
      options: {
        en: ["Troublemaker", "Brainiac", "Peacemaker", "Artist", "Leader", "Rebel"],
        zh: ["问题制造者", "智囊团", "和事佬", "艺术家", "领导者", "叛逆者"],
        ru: ["Нарушитель спокойствия", "Умник", "Миротворец", "Художник", "Лидер", "Бунтарь"],
        fr: ["Fauteur de troubles", "Cerveau", "Pacificateur", "Artiste", "Leader", "Rebelle"],
        de: ["Unruhestifter", "Genie", "Friedensstifter", "Künstler", "Anführer", "Rebell"],
        nl: ["Onruststoker", "Brein", "Vredestichter", "Kunstenaar", "Leider", "Rebel"],
        ms: ["Pembuat masalah", "Genius", "Pendamai", "Seniman", "Pemimpin", "Pemberontak"]
      },
      characters: ['Reese', 'Malcolm', 'Hal', 'Dewey', 'Lois', 'Francis'] as CharacterName[]
    },
    {
      question: "lifestyle",
      options: {
        en: ["Free-spirited", "Excellence-driven", "Peaceful", "Artistic", "Organized", "Adventurous"],
        zh: ["自由自在", "追求卓越", "平静安稳", "充满艺术", "井井有条", "充满冒险"],
        ru: ["Свободолюбивый", "Стремящийся к совершенству", "Мирный", "Артистичный", "Организованный", "Авантюрный"],
        fr: ["Esprit libre", "Perfectionniste", "Paisible", "Artistique", "Organisé", "Aventureux"],
        de: ["Freigeistig", "Leistungsorientiert", "Friedlich", "Künstlerisch", "Organisiert", "Abenteuerlustig"],
        nl: ["Vrijgevochten", "Excellentiegericht", "Vredig", "Artistiek", "Georganiseerd", "Avontuurlijk"],
        ms: ["Bebas", "Mementingkan kecemerlangan", "Aman", "Artistik", "Teratur", "Penuh cabaran"]
      },
      characters: ['Francis', 'Malcolm', 'Hal', 'Dewey', 'Lois', 'Reese'] as CharacterName[]
    },
    {
      question: "rules",
      options: {
        en: ["Follow rules", "Question rules", "Bend rules", "Ignore rules", "Make rules", "Break rules"],
        zh: ["遵守规则", "质疑规则", "变通规则", "忽视规则", "制定规则", "打破规则"],
        ru: ["Следовать правилам", "Подвергать сомнению правила", "Обходить правила", "Игнорировать правила", "Создавать правила", "Нарушать правила"],
        fr: ["Suivre les règles", "Questionner les règles", "Contourner les règles", "Ignorer les règles", "Faire les règles", "Enfreindre les règles"],
        de: ["Regeln befolgen", "Regeln hinterfragen", "Regeln biegen", "Regeln ignorieren", "Regeln aufstellen", "Regeln brechen"],
        nl: ["Regels volgen", "Regels in twijfel trekken", "Regels buigen", "Regels negeren", "Regels maken", "Regels breken"],
        ms: ["Ikut peraturan", "Persoal peraturan", "Lentur peraturan", "Abaikan peraturan", "Buat peraturan", "Langgar peraturan"]
      },
      characters: ['Hal', 'Malcolm', 'Dewey', 'Reese', 'Lois', 'Francis'] as CharacterName[]
    },
    {
      question: "hobby",
      options: {
        en: ["Try new things", "Think about problems", "Create music", "Play sports", "Organize activities", "Explore the world"],
        zh: ["尝试新事物", "思考问题", "创作音乐", "体育运动", "组织活动", "探索世界"],
        ru: ["Пробовать новое", "Размышлять о проблемах", "Создавать музыку", "Заниматься спортом", "Организовывать мероприятия", "Исследовать мир"],
        fr: ["Essayer de nouvelles choses", "Réfléchir aux problèmes", "Créer de la musique", "Faire du sport", "Organiser des activités", "Explorer le monde"],
        de: ["Neues ausprobieren", "Über Probleme nachdenken", "Musik machen", "Sport treiben", "Aktivitäten organisieren", "Die Welt erkunden"],
        nl: ["Nieuwe dingen proberen", "Over problemen nadenken", "Muziek maken", "Sporten", "Activiteiten organiseren", "De wereld verkennen"],
        ms: ["Cuba perkara baru", "Fikir tentang masalah", "Cipta muzik", "Main sukan", "Atur aktiviti", "Jelajah dunia"]
      },
      characters: ['Hal', 'Malcolm', 'Dewey', 'Reese', 'Lois', 'Francis'] as CharacterName[]
    }
  ];

  const seasonQuotes = [
    {
      season: 1,
      quotes: [
        {
          text: "生活是不公平的，习惯就好。",
          textEn: "Life is unfair, get used to it.",
          textRu: "Жизнь несправедлива, привыкай к этому.",
          textFr: "La vie est injuste, habituez-vous-y.",
          textDe: "Das Leben ist unfair, gewöhn dich dran.",
          textNl: "Het leven is oneerlijk, wen er maar aan.",
          textMs: "Hidup tidak adil, biasakanlah.",
          character: "Lois",
          episode: "试播集",
          episodeEn: "Pilot",
          episodeRu: "Пилотный выпуск",
          episodeFr: "Épisode pilote",
          episodeDe: "Pilotfolge",
          episodeNl: "Pilotaflevering",
          episodeMs: "Episod Pilot"
        },
        {
          text: "我讨厌成为天才，这让我感觉很孤独。",
          textEn: "I hate being a genius, it's so lonely.",
          textRu: "Я ненавижу быть гением, это так одиноко.",
          textFr: "Je déteste être un génie, c'est si solitaire.",
          textDe: "Ich hasse es, ein Genie zu sein, es ist so einsam.",
          textNl: "Ik haat het om een genie te zijn, het is zo eenzaam.",
          textMs: "Saya benci menjadi genius, ia sangat sunyi.",
          character: "Malcolm",
          episode: "天才班",
          episodeEn: "Krelboyne Class",
          episodeRu: "Класс для одаренных",
          episodeFr: "Classe des surdoués",
          episodeDe: "Hochbegabtenklasse",
          episodeNl: "Krelboyne klas",
          episodeMs: "Kelas Genius"
        }
      ]
    },
    {
      season: 2,
      quotes: [
        {
          text: "当中产阶级的中间孩子已经够惨了，但更惨的是成为中产中间孩子的中间孩子。",
          textEn: "The only thing worse than being the middle child is being the middle child of a middle child.",
          textRu: "Хуже среднего ребенка может быть только средний ребенок среднего ребенка.",
          textFr: "La seule chose pire qu'être l'enfant du milieu, c'est d'être l'enfant du milieu d'un enfant du milieu.",
          textDe: "Das einzig Schlimmere als das mittlere Kind zu sein, ist das mittlere Kind eines mittleren Kindes zu sein.",
          textNl: "Het enige wat erger is dan het middelste kind zijn, is het middelste kind van een middelste kind zijn.",
          textMs: "Satu-satunya perkara yang lebih teruk daripada menjadi anak tengah ialah menjadi anak tengah kepada anak tengah.",
          character: "Malcolm",
          episode: "身份认同",
          episodeEn: "Identity Crisis",
          episodeRu: "Кризис личности",
          episodeFr: "Crise d'identité",
          episodeDe: "Identitätskrise",
          episodeNl: "Identiteitscrisis",
          episodeMs: "Krisis Identiti"
        },
        {
          text: "我不是在找借口，我是在解释为什么这不是我的错。",
          textEn: "I'm not making excuses, I'm explaining why this isn't my fault.",
          textRu: "Я не ищу оправданий, я объясняю, почему это не моя вина.",
          textFr: "Je ne cherche pas d'excuses, j'explique pourquoi ce n'est pas ma faute.",
          textDe: "Ich suche keine Ausreden, ich erkläre, warum das nicht meine Schuld ist.",
          textNl: "Ik zoek geen excuses, ik leg uit waarom dit niet mijn schuld is.",
          textMs: "Saya tidak membuat alasan, saya menjelaskan mengapa ini bukan salah saya.",
          character: "Reese",
          episode: "保龄球",
          episodeEn: "Bowling",
          episodeRu: "Боулинг",
          episodeFr: "Bowling",
          episodeDe: "Bowling",
          episodeNl: "Bowlen",
          episodeMs: "Boling"
        }
      ]
    },
    {
      season: 3,
      quotes: [
        {
          text: "我们不是坏孩子，我们只是有创造力。",
          textEn: "We're not bad kids, we're just creative.",
          textRu: "Мы не плохие дети, мы просто творческие.",
          textFr: "Nous ne sommes pas de mauvais enfants, nous sommes juste créatifs.",
          textDe: "Wir sind keine schlechten Kinder, wir sind nur kreativ.",
          textNl: "We zijn geen slechte kinderen, we zijn gewoon creatief.",
          textMs: "Kami bukan budak-budak nakal, kami hanya kreatif.",
          character: "Malcolm",
          episode: "家庭度假",
          episodeEn: "Family Vacation",
          episodeRu: "Семейный отпуск",
          episodeFr: "Vacances en famille",
          episodeDe: "Familienurlaub",
          episodeNl: "Familievakantie",
          episodeMs: "Percutian Keluarga"
        },
        {
          text: "在这个家里，正常才是不正常的。",
          textEn: "In this house, normal is the abnormal.",
          textRu: "В этом доме нормальное - это ненормальное.",
          textFr: "Dans cette maison, le normal est l'anormal.",
          textDe: "In diesem Haus ist normal das Unnormale.",
          textNl: "In dit huis is normaal het abnormale.",
          textMs: "Di rumah ini, normal adalah tidak normal.",
          character: "Dewey",
          episode: "Lois怀孕",
          episodeEn: "Lois's Pregnancy",
          episodeRu: "Беременность Лоис",
          episodeFr: "La grossesse de Lois",
          episodeDe: "Lois' Schwangerschaft",
          episodeNl: "Lois' zwangerschap",
          episodeMs: "Kehamilan Lois"
        }
      ]
    },
    {
      season: 4,
      quotes: [
        {
          text: "未来就是现在，老头子。",
          textEn: "The future is now, old man.",
          textRu: "Будущее уже здесь, старик.",
          textFr: "Le futur c'est maintenant, vieil homme.",
          textDe: "Die Zukunft ist jetzt, alter Mann.",
          textNl: "De toekomst is nu, oude man.",
          textMs: "Masa depan adalah sekarang, orang tua.",
          character: "Dewey",
          episode: "Hal的赛车",
          episodeEn: "Hal's Race Car",
          episodeRu: "Гоночная машина Хэла",
          episodeFr: "La voiture de course de Hal",
          episodeDe: "Hals Rennauto",
          episodeNl: "Hal's raceauto",
          episodeMs: "Kereta Lumba Hal"
        },
        {
          text: "有时候我觉得我是这个家里唯一一个疯的。",
          textEn: "Sometimes I feel like I'm the only crazy one in this house.",
          textRu: "Иногда мне кажется, что я единственный сумасшедший в этом доме.",
          textFr: "Parfois, j'ai l'impression d'être le seul fou dans cette maison.",
          textDe: "Manchmal fühle ich mich, als wäre ich der einzige Verrückte in diesem Haus.",
          textNl: "Soms voel ik me alsof ik de enige gekke in dit huis ben.",
          textMs: "Kadang-kadang saya rasa saya satu-satunya yang gila di rumah ini.",
          character: "Malcolm",
          episode: "心理治疗",
          episodeEn: "Therapy",
          episodeRu: "Терапия",
          episodeFr: "Thérapie",
          episodeDe: "Therapie",
          episodeNl: "Therapie",
          episodeMs: "Terapi"
        }
      ]
    },
    {
      season: 5,
      quotes: [
        {
          text: "我不需要朋友，我有家人。",
          textEn: "I don't need friends, I have family.",
          textRu: "Мне не нужны друзья, у меня есть семья.",
          textFr: "Je n'ai pas besoin d'amis, j'ai ma famille.",
          textDe: "Ich brauche keine Freunde, ich habe Familie.",
          textNl: "Ik heb geen vrienden nodig, ik heb familie.",
          textMs: "Saya tidak perlukan kawan, saya ada keluarga.",
          character: "Reese",
          episode: "参军",
          episodeEn: "Army",
          episodeRu: "Армия",
          episodeFr: "L'armée",
          episodeDe: "Armee",
          episodeNl: "Leger",
          episodeMs: "Tentera"
        },
        {
          text: "有时候最聪明的做法就是装傻。",
          textEn: "Sometimes the smartest thing to do is act dumb.",
          textRu: "Иногда самое умное - это прикинуться глупым.",
          textFr: "Parfois, la chose la plus intelligente à faire est de jouer l'idiot.",
          textDe: "Manchmal ist es am klügsten, sich dumm zu stellen.",
          textNl: "Soms is het het slimste om dom te doen.",
          textMs: "Kadang-kadang perkara yang paling bijak adalah berpura-pura bodoh.",
          character: "Malcolm",
          episode: "工作",
          episodeEn: "Job",
          episodeRu: "Работа",
          episodeFr: "Le travail",
          episodeDe: "Arbeit",
          episodeNl: "Werk",
          episodeMs: "Kerja"
        }
      ]
    },
    {
      season: 6,
      quotes: [
        {
          text: "音乐是我逃离这个疯狂世界的方式。",
          textEn: "Music is my way of escaping this crazy world.",
          textRu: "Музыка - мой способ убежать от этого сумасшедшего мира.",
          textFr: "La musique est ma façon d'échapper à ce monde fou.",
          textDe: "Musik ist meine Art, dieser verrückten Welt zu entfliehen.",
          textNl: "Muziek is mijn manier om aan deze gekke wereld te ontsnappen.",
          textMs: "Muzik adalah cara saya melarikan diri dari dunia gila ini.",
          character: "Dewey",
          episode: "音乐剧",
          episodeEn: "Musical",
          episodeRu: "Мюзикл",
          episodeFr: "La comédie musicale",
          episodeDe: "Musical",
          episodeNl: "Musical",
          episodeMs: "Muzikal"
        },
        {
          text: "我宁愿做一个快乐的傻子，也不要做一个痛苦的天才。",
          textEn: "I'd rather be a happy fool than a miserable genius.",
          textRu: "Лучше быть счастливым дураком, чем несчастным гением.",
          textFr: "Je préfère être un idiot heureux qu'un génie malheureux.",
          textDe: "Lieber ein glücklicher Narr als ein unglückliches Genie.",
          textNl: "Ik ben liever een gelukkige dwaas dan een ongelukkig genie.",
          textMs: "Saya lebih suka menjadi orang bodoh yang gembira daripada genius yang sengsara.",
          character: "Malcolm",
          episode: "大学准备",
          episodeEn: "College Prep",
          episodeRu: "Подготовка к колледжу",
          episodeFr: "Préparation à l'université",
          episodeDe: "College-Vorbereitung",
          episodeNl: "Universitaire voorbereiding",
          episodeMs: "Persediaan Kolej"
        }
      ]
    },
    {
      season: 7,
      quotes: [
        {
          text: "你知道为什么我们总能逃过惩罚吗？因为我们是一家人。",
          textEn: "You know why we always get away with it? Because we're family.",
          textRu: "Знаешь, почему нам всегда все сходит с рук? Потому что мы семья.",
          textFr: "Tu sais pourquoi on s'en sort toujours ? Parce qu'on est une famille.",
          textDe: "Weißt du, warum wir immer damit durchkommen? Weil wir eine Familie sind.",
          textNl: "Weet je waarom we er altijd mee wegkomen? Omdat we familie zijn.",
          textMs: "Tahu kenapa kita selalu terlepas? Kerana kita keluarga.",
          character: "Malcolm",
          episode: "最后一集",
          episodeEn: "Final Episode",
          episodeRu: "Финальный эпизод",
          episodeFr: "Épisode final",
          episodeDe: "Letzte Folge",
          episodeNl: "Laatste aflevering",
          episodeMs: "Episod Akhir"
        },
        {
          text: "生活确实不公平，但这才让它变得有趣。",
          textEn: "Life is unfair, but that's what makes it interesting.",
          textRu: "Жизнь несправедлива, но именно это делает её интересной.",
          textFr: "La vie est injuste, mais c'est ce qui la rend intéressante.",
          textDe: "Das Leben ist unfair, aber das macht es interessant.",
          textNl: "Het leven is oneerlijk, maar dat maakt het interessant.",
          textMs: "Hidup memang tidak adil, tapi itulah yang membuatnya menarik.",
          character: "Lois",
          episode: "结局",
          episodeEn: "Finale",
          episodeRu: "Финал",
          episodeFr: "Final",
          episodeDe: "Finale",
          episodeNl: "Finale",
          episodeMs: "Final"
        }
      ]
    }
  ];

  const seasons = [
    {
      number: 1,
      episodes: 16,
      year: "2000",
      highlights: ["Malcolm的天才身份被发现", "Francis在军事学校的生活", "Dewey的音乐天赋初现"],
      highlightsEn: ["Malcolm's genius identity discovered", "Francis's life in military school", "Dewey's musical talent emerges"]
    },
    {
      number: 2,
      episodes: 25,
      year: "2000-2001",
      highlights: ["Hal的保龄球比赛", "Reese的厨艺天赋", "Malcolm的第一次约会"],
      highlightsEn: ["Hal's bowling competition", "Reese's culinary talent", "Malcolm's first date"]
    },
    {
      number: 3,
      episodes: 22,
      year: "2001-2002",
      highlights: ["家庭度假的灾难", "Lois怀孕", "Francis在阿拉斯加工作"],
      highlightsEn: ["Family vacation disaster", "Lois's pregnancy", "Francis working in Alaska"]
    },
    {
      number: 4,
      episodes: 22,
      year: "2002-2003",
      highlights: ["Jamie的出生", "Malcolm的心理治疗", "Hal的赛车梦想"],
      highlightsEn: ["Jamie's birth", "Malcolm's therapy", "Hal's race car dream"]
    },
    {
      number: 5,
      episodes: 22,
      year: "2003-2004",
      highlights: ["Francis的牧场生活", "Reese参军", "Malcolm的工作经历"],
      highlightsEn: ["Francis's ranch life", "Reese joins the army", "Malcolm's work experience"]
    },
    {
      number: 6,
      episodes: 22,
      year: "2004-2005",
      highlights: ["Ida搬来同住", "Dewey的音乐剧", "Malcolm的大学准备"],
      highlightsEn: ["Ida moves in", "Dewey's musical", "Malcolm's college preparation"]
    },
    {
      number: 7,
      episodes: 22,
      year: "2005-2006",
      highlights: ["Malcolm的哈佛录取", "Reese成为管理员", "全家人的最后告别"],
      highlightsEn: ["Malcolm's Harvard acceptance", "Reese becomes a janitor", "The family's final goodbye"]
    }
  ];

  // 添加SEO相关的meta标签
  React.useEffect(() => {
    document.title = 'Malcolm in the Middle - Character Quiz & Show Info';
    
    // 更新meta标签
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Explore Malcolm in the Middle characters, quotes, and find out which character matches your personality. A fan site dedicated to the classic family comedy show.');
    }

    // 添加Open Graph标签
    const ogTitle = document.createElement('meta');
    ogTitle.setAttribute('property', 'og:title');
    ogTitle.setAttribute('content', 'Malcolm in the Middle Fan Site');
    document.head.appendChild(ogTitle);

    const ogDescription = document.createElement('meta');
    ogDescription.setAttribute('property', 'og:description');
    ogDescription.setAttribute('content', 'Discover your Malcolm in the Middle character match and explore the show\'s best moments.');
    document.head.appendChild(ogDescription);

    // 清理函数
    return () => {
      document.head.removeChild(ogTitle);
      document.head.removeChild(ogDescription);
    };
  }, []);

  return (
    <LanguageProvider>
      <BrowserRouter>
        <Layout>
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/characters" element={<Characters />} />
            <Route path="/quotes" element={<Quotes quotes={seasonQuotes} />} />
            <Route path="/quiz" element={<Quiz questions={questions} />} />
          </Routes>
        </Layout>
      </BrowserRouter>
    </LanguageProvider>
  );
}

export default App;